/** Carrying "this half-session is for a workspace you just chose" across the reload the switcher
 * makes (G-P2-02).
 *
 * **The defect this exists to close.** `POST /auth/switch` into a workspace where the person's role
 * needs a second factor answers 200 with `mfa_required`, sets a fresh `PENDING_MFA` cookie and has
 * already revoked the old session. The switcher ignored the body and reloaded the page; `GET
 * /auth/me` answers 401 for a half-session, so the shell showed the bare email-and-password form
 * with no code box and no sentence. The server was waiting for a code the screen never asked for,
 * and signing in again resolves the *first* workspace by name, so an Admin or Approver seat in a
 * second workspace could not be reached from a first one in which the person needs no factor.
 *
 * **What is stored** is the answer the server gave, the name of the workspace it was for, and when:
 * whether the code step is an enrolment, a legal name the person chose a moment ago, and a clock
 * reading. No token (the session is an httpOnly cookie this code never sees), no address, no
 * figure. `sessionStorage` rather than `localStorage` for `sessionEnded.ts`'s reason: it dies with
 * the tab, so the hint cannot turn up for a shared machine's next visitor.
 *
 * It is a **hint about which step to show**, not a credential and not an authorisation: the server
 * decides whether a code is accepted. It expires when the half-session does (five minutes,
 * `PENDING_MFA_LIFETIME`), so a hint left behind by a tab that was closed on the code step cannot
 * greet a later visit with a code box and no session; and it is dropped by "Use a different
 * account", by a fresh password submission and by a completed sign-in.
 *
 * Storage can be refused, so every access is in `try/catch` and an in-memory copy carries the hint
 * for this page load; the reload the switcher makes would lose it, which is the browser's refusal
 * and no worse than the behaviour this replaces.
 */

export interface SwitchPending {
  /** The second factor has not been set up yet: the first step is enrolment, not a code. */
  enrolmentRequired: boolean;
  /** The workspace the person chose, in its legal name, so the step can say what it is for. */
  workspace: string;
}

interface Stored extends SwitchPending {
  at: number;
}

const KEY = "emitiq.switch-pending";

/** Matches the server's half-session: after this, the code box would be for a session that is gone. */
const LIFETIME_MS = 5 * 60 * 1000;

let inMemory: Stored | null = null;

function live(stored: Stored | null, now: number): SwitchPending | null {
  if (!stored || now - stored.at > LIFETIME_MS || now < stored.at) return null;
  return { enrolmentRequired: stored.enrolmentRequired, workspace: stored.workspace };
}

/** Remember that the session now in the cookie is a half-session for `pending.workspace`. */
export function markSwitchPending(pending: SwitchPending, now: number = Date.now()): void {
  const stored: Stored = { ...pending, at: now };
  inMemory = stored;
  try {
    window.sessionStorage.setItem(KEY, JSON.stringify(stored));
  } catch {
    // Storage refused: the in-memory copy still carries it for this page load.
  }
}

/** The pending switch, if one is still live. Pure: reading does not clear it. */
export function switchPendingRead(now: number = Date.now()): SwitchPending | null {
  const fromMemory = live(inMemory, now);
  if (fromMemory) return fromMemory;
  try {
    const raw = window.sessionStorage.getItem(KEY);
    if (!raw) return null;
    const parsed: unknown = JSON.parse(raw);
    if (
      parsed &&
      typeof parsed === "object" &&
      "enrolmentRequired" in parsed &&
      "workspace" in parsed &&
      "at" in parsed &&
      typeof parsed.enrolmentRequired === "boolean" &&
      typeof parsed.workspace === "string" &&
      typeof parsed.at === "number"
    ) {
      return live(
        { enrolmentRequired: parsed.enrolmentRequired, workspace: parsed.workspace, at: parsed.at },
        now,
      );
    }
  } catch {
    // Unreadable or refused: no hint, which is the ordinary sign-in form.
  }
  return null;
}

/** Lower the hint: the person left the step, or finished it. */
export function clearSwitchPending(): void {
  inMemory = null;
  try {
    window.sessionStorage.removeItem(KEY);
  } catch {
    // Nothing to clear if storage is refused.
  }
}
